import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@marine/api-client/api';

/**
 * Platform settings — singleton row in the landlord DB (#824). Holds
 * platform-managed third-party client keys (currently the Syncfusion
 * license). Every API payload here ships a masked tail only (`…abcd`) —
 * raw key material never traverses the wire after the admin types it in.
 */
export interface PlatformSettings {
  id: string;
  syncfusion_license_masked: string | null;
  has_key: boolean;
  updated_at: string | null;
  /** #597 — resolved server-side; raw user IDs never render in the UI. */
  updated_by: { id: string; full_name: string } | null;
}

export interface UpdatePlatformSettingsInput {
  syncfusion_license_key?: string | null;
}

// `api.getOne` / `api.putOne` already unwrap the `{data: ...}` envelope, so
// these signatures and the hooks below DO NOT need a `select` step.
export const platformSettingsApi = {
  show: (): Promise<PlatformSettings> =>
    api.getOne<PlatformSettings>('/api/platform/settings'),
  update: (input: UpdatePlatformSettingsInput): Promise<PlatformSettings> =>
    api.putOne<PlatformSettings>('/api/platform/settings', input),
};

export function usePlatformSettings() {
  return useQuery({
    queryKey: ['platform-settings'],
    queryFn: () => platformSettingsApi.show(),
  });
}

export function useUpdatePlatformSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdatePlatformSettingsInput) => platformSettingsApi.update(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform-settings'] });
    },
  });
}
